import { useState, useEffect } from 'react'
import { useETSFDocuments, type ETSFDocument } from '../../../hooks/useETSFDocuments'
import { useAuth } from '../../../contexts/AuthContext'
import { hasAnyRole } from '../../../lib/roles'

const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'

interface SOPViewProps {
  location?: 'BHX' | 'LHR'
}

const renderMarkdown = (markdown: string) => {
  const lines = markdown.split('\n')
  const elements = []
  let currentParagraph: string[] = []

  for (const line of lines) {
    if (line.startsWith('# ')) {
      if (currentParagraph.length) {
        elements.push(
          <p key={`p-${elements.length}`} className="mb-4">
            {currentParagraph.join('\n')}
          </p>
        )
        currentParagraph = []
      }
      elements.push(<h1 key={`h1-${elements.length}`} className="text-3xl font-bold mt-8 mb-4">{line.replace(/^# /, '')}</h1>)
    } else if (line.startsWith('## ')) {
      if (currentParagraph.length) {
        elements.push(
          <p key={`p-${elements.length}`} className="mb-4">
            {currentParagraph.join('\n')}
          </p>
        )
        currentParagraph = []
      }
      elements.push(<h2 key={`h2-${elements.length}`} className="text-2xl font-bold mt-6 mb-3">{line.replace(/^## /, '')}</h2>)
    } else if (line.startsWith('### ')) {
      if (currentParagraph.length) {
        elements.push(
          <p key={`p-${elements.length}`} className="mb-4">
            {currentParagraph.join('\n')}
          </p>
        )
        currentParagraph = []
      }
      elements.push(<h3 key={`h3-${elements.length}`} className="text-lg font-semibold mt-4 mb-2">{line.replace(/^### /, '')}</h3>)
    } else if (line.startsWith('> ')) {
      if (currentParagraph.length) {
        elements.push(
          <p key={`p-${elements.length}`} className="mb-4">
            {currentParagraph.join('\n')}
          </p>
        )
        currentParagraph = []
      }
      elements.push(
        <blockquote key={`quote-${elements.length}`} className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20 p-4 my-4">
          {line.replace(/^> /, '')}
        </blockquote>
      )
    } else if (line.startsWith('| ')) {
      if (currentParagraph.length) {
        elements.push(
          <p key={`p-${elements.length}`} className="mb-4">
            {currentParagraph.join('\n')}
          </p>
        )
        currentParagraph = []
      }
      // Simple table rendering - collect table lines
      const tableLines = [line]
      let nextIdx = lines.indexOf(line) + 1
      while (nextIdx < lines.length && lines[nextIdx].startsWith('| ')) {
        tableLines.push(lines[nextIdx])
        nextIdx++
      }

      if (tableLines.length > 0) {
        const rows = tableLines.map(l => l.split('|').filter(c => c.trim()).map(c => c.trim()))
        elements.push(
          <table key={`table-${elements.length}`} className="w-full border-collapse my-4 text-sm">
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className={idx === 1 ? 'border-b-2 border-gray-300 dark:border-gray-600' : 'border-b border-gray-200 dark:border-gray-700'}>
                  {row.map((cell, cidx) => (
                    <td
                      key={cidx}
                      className={`px-3 py-2 text-left ${idx === 0 ? 'font-semibold bg-gray-100 dark:bg-gray-700' : ''}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    } else if (line.trim() === '') {
      if (currentParagraph.length) {
        elements.push(
          <p key={`p-${elements.length}`} className="mb-4">
            {currentParagraph.join('\n')}
          </p>
        )
        currentParagraph = []
      }
    } else {
      currentParagraph.push(line)
    }
  }

  if (currentParagraph.length) {
    elements.push(
      <p key={`p-${elements.length}`} className="mb-4">
        {currentParagraph.join('\n')}
      </p>
    )
  }

  return elements
}

export default function SOPView({ location = 'LHR' }: SOPViewProps) {
  const { userRoles, userEmail } = useAuth()
  const { documents, loading, error, updateDocument } = useETSFDocuments()
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [document, setDocument] = useState<ETSFDocument | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<'lhr' | 'bhx'>(location === 'LHR' ? 'lhr' : 'bhx')

  useEffect(() => {
    setSelectedLocation(location === 'LHR' ? 'lhr' : 'bhx')
  }, [location])

  useEffect(() => {
    if (documents.length > 0) {
      const docType = selectedLocation === 'lhr' ? 'sop' : 'sop_bhx'
      const selectedDoc = documents.find(d => d.doc_type === docType) || documents[0]
      setDocument(selectedDoc)
      setEditContent(selectedDoc.content)
    }
  }, [documents, selectedLocation])

  const handleSave = async () => {
    if (!document) return
    setSaving(true)
    try {
      const updated = await updateDocument(document.id, editContent, userEmail || 'System')
      setDocument(updated)
      setIsEditing(false)
    } catch (err: any) {
      alert('Error saving document: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  const isAdmin = hasAnyRole(userRoles, ['ADMIN', 'OPERATIONS'])

  if (loading) return <div className="p-6">Loading...</div>
  if (error) return <div className="p-6 text-red-600">{error}</div>
  if (!document) return <div className="p-6">No SOP document found</div>

  return (
    <>
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">{document.title}</h2>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
              <p>Version: {document.version}</p>
              <p>Last updated: {new Date(document.updated_at).toLocaleDateString('en-GB')} by {document.last_updated_by || 'System'}</p>
            </div>
          </div>
          <div className="flex gap-2">
          {isAdmin && (
            <>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setEditContent(document.content)
                      setIsEditing(false)
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </>
              )}
            </>
          )}
          <button
            onClick={handlePrint}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Print / PDF
          </button>
          <a
            href={
              location === 'BHX'
                ? 'https://docs.google.com/document/d/1o6Id9T1lncHRsCPvDuiDcFn_ELNrhjTo/edit?usp=drive_link&ouid=114585408859775878478&rtpof=true&sd=true'
                : 'https://docs.google.com/document/d/1VsSWRfS41r2dn_U_zWsAQSbDuZKCglLk/edit?usp=drive_link&ouid=114585408859775878478&rtpof=true&sd=true'
            }
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 border border-green-500 text-green-600 dark:border-green-400 dark:text-green-400 rounded hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Download DOCX
          </a>
        </div>
        </div>
      </div>

      {isEditing ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <textarea
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            className={INPUT_CLS + ' font-mono h-96'}
            placeholder="SOP content (markdown format)"
          />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 prose dark:prose-invert max-w-none">
          {renderMarkdown(document.content)}
        </div>
      )}

      <style>{`
        @media print {
          body { background: white; }
          .no-print { display: none; }
        }
      `}</style>
    </>
  )
}
